import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { BackButton } from './BaseTestStyles';
import { readingTests } from '../../data/readingTests';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  padding: 1.5rem;
`;

const TestSelection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  padding: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 1rem 0.5rem;
  }
`;

const TestCard = styled.div`
  background: ${props => props.isCompleted ? '#f8fbff' : 'white'};
  border-radius: 12px;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: ${props => props.isCompleted ? '1px solid #3498db' : '1px solid #eee'};
  position: relative;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  h3 {
    font-size: 1.25rem;
    margin-bottom: 1rem;
    color: #2c3e50;
  }

  p {
    color: #666;
    line-height: 1.5;
    margin-bottom: 1.5rem;
  }
`;

const CompletionBadge = styled.div`
  background: ${props => props.score >= 7 ? '#e8f7f0' : props.score >= 5 ? '#fff7e6' : '#fee8e7'};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: ${props => props.score >= 7 ? '#27ae60' : props.score >= 5 ? '#f39c12' : '#c0392b'};
  font-weight: 500;
  margin-bottom: 1rem;
`;

const TestContent = styled.div`
  max-width: 1400px;
  margin: 1rem auto;
  display: grid;
  grid-template-columns: 5fr 4fr;
  gap: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    margin: 0.5rem;
    height: auto;
  }
`;

const ReadingPassage = styled.div`
  padding: 2.5rem;
  height: calc(100vh - 10rem);
  overflow-y: auto;
  border-right: 1px solid #eee;

  @media (max-width: 768px) {
    padding: 1.5rem;
    height: auto;
    max-height: 50vh;
    border-right: none;
    border-bottom: 1px solid #eee;
  }

  h2 {
    font-size: 1.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }

  h3 {
    font-size: 1.2rem;
    color: #34495e;
    margin: 1.5rem 0 1rem;
  }

  p {
    line-height: 1.8;
    color: #2c3e50;
    font-size: 1.1rem;
    margin-bottom: 1.5rem;

    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const QuestionsPanel = styled.div`
  padding: 2.5rem;
  height: calc(100vh - 10rem);
  overflow-y: auto;
  background: #fafafa;

  @media (max-width: 768px) {
    padding: 1.5rem;
    height: auto;
    max-height: 50vh;
  }

  h2 {
    font-size: 1.4rem;
    color: #2c3e50;
    margin-bottom: 2rem;
  }
`;

const Timer = styled.div`
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  background: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-weight: 600;
  font-size: 1.1rem;
  color: ${props => props.timeLeft < 300 ? '#e74c3c' : '#2c3e50'};
  border: ${props => props.timeLeft < 300 ? '1px solid #e74c3c' : '1px solid #eee'};

  @media (max-width: 768px) {
    position: sticky;
    top: 0;
    right: 0;
    margin: 0.5rem auto;
    width: fit-content;
    z-index: 100;
  }
`;

const Question = styled.div`
  margin-bottom: 2.5rem;
  padding: 1.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);

  h3 {
    font-size: 1.1rem;
    color: #2c3e50;
    margin-bottom: 1.2rem;
    line-height: 1.5;
  }

  label {
    display: block;
    padding: 0.8rem;
    margin: 0.8rem 0;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #f8f9fa;
    }

    input {
      margin-right: 0.8rem;
    }
  }
`;

const ResultsContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 3rem;
  max-width: 900px;
  margin: 2rem auto;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 1rem;
  }
`;

const ScoreDisplay = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #eee;
  
  h2 {
    color: ${props => props.score >= 7 ? '#27ae60' : props.score >= 5 ? '#f39c12' : '#c0392b'};
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }

  p {
    color: #666;
    font-size: 1.1rem;
  }
`;

const AnswerReview = styled.div`
  margin-top: 2rem;

  h3 {
    font-size: 1.4rem;
    color: #2c3e50;
    margin-bottom: 2rem;
  }
`;

const TopBar = styled.div`
  background: #003b70;
  padding: 1rem 2rem;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: 600;
`;

const LogoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  color: #003b70;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  font-size: 1.25rem;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ReadingTest = ({ onBack = () => {} }) => {
  const navigate = useNavigate();
  const [selectedTest, setSelectedTest] = useState(null);
  const [timeLeft, setTimeLeft] = useState(1080); // 18 minutes
  const [userAnswers, setUserAnswers] = useState({});
  const [isTestComplete, setIsTestComplete] = useState(false);
  const [score, setScore] = useState(null);
  const [completedTests, setCompletedTests] = useState(() => {
    const saved = localStorage.getItem('completedReadingTests');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('completedReadingTests', JSON.stringify(completedTests));
  }, [completedTests]);

  React.useEffect(() => {
    if (selectedTest && timeLeft > 0 && !isTestComplete) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsTestComplete(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [selectedTest, timeLeft, isTestComplete]);

  const calculateScore = () => {
    if (!selectedTest) return 0;
    let correct = 0;
    selectedTest.questions.forEach((question, index) => {
      if (userAnswers[`q${index}`] === question.correctAnswer) {
        correct++;
      }
    });
    return correct;
  };

  const handleSubmitTest = () => {
    const finalScore = calculateScore();
    setScore(finalScore);
    setIsTestComplete(true);
    
    setCompletedTests(prev => ({
      ...prev,
      [selectedTest.id]: {
        completed: true,
        score: finalScore,
        date: new Date().toISOString()
      }
    }));
  };

  const handleBack = () => {
    if (selectedTest) {
      setSelectedTest(null);
      setTimeLeft(1080);
      setUserAnswers({});
      setIsTestComplete(false);
      setScore(null);
    } else {
      navigate('/');
    }
  };

  if (isTestComplete && selectedTest) {
    return (
      <Container>
        <TopBar>
          <Logo>
            <LogoIcon>TT</LogoIcon>
            TOEFL Top
          </Logo>
          <BackButton onClick={handleBack}>← Back to Test Selection</BackButton>
        </TopBar>
        <ResultsContainer>
          <ScoreDisplay score={score}>
            <h2>Your Score: {score}/10</h2>
            <p>Time Taken: {Math.floor((1080 - timeLeft) / 60)}:{((1080 - timeLeft) % 60).toString().padStart(2, '0')}</p>
          </ScoreDisplay>
          
          <AnswerReview>
            <h3>Review Your Answers:</h3>
            {selectedTest.questions.map((question, index) => (
              <div key={index} style={{ margin: '1.5rem 0', padding: '1.5rem', background: '#f8f9fa', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <p style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: '1rem' }}><strong>Question {index + 1}:</strong> {question.question}</p>
                <p style={{ margin: '0.8rem 0', color: '#444' }}>Your Answer: {userAnswers[`q${index}`] !== undefined ? 
                  question.options[userAnswers[`q${index}`]] : 'Not answered'}</p>
                <p style={{ margin: '0.8rem 0', color: '#444' }}>Correct Answer: {question.options[question.correctAnswer]}</p>
                <p style={{ 
                  color: userAnswers[`q${index}`] === question.correctAnswer ? '#27ae60' : '#c0392b',
                  margin: '0.8rem 0',
                  fontWeight: 500
                }}>
                  {userAnswers[`q${index}`] === question.correctAnswer ? '✓ Correct' : '✗ Incorrect'}
                </p>
                <p style={{ color: '#666', marginTop: '1rem', fontStyle: 'italic', lineHeight: 1.6 }}>
                  Explanation: {question.explanation}
                </p>
              </div>
            ))}
          </AnswerReview>
        </ResultsContainer>
      </Container>
    );
  }

  if (!selectedTest) {
    return (
      <Container>
        <TopBar>
          <Logo>
            <LogoIcon>TT</LogoIcon>
            TOEFL Top
          </Logo>
          <BackButton onClick={handleBack}>← Back to Test Selection</BackButton>
        </TopBar>
        <h1 style={{ color: '#2c3e50', textAlign: 'center', margin: '2rem 0', fontSize: '2rem' }}>
          Select a Reading Test
        </h1>
        <TestSelection>
          {readingTests.map((test) => (
            <TestCard 
              key={test.id} 
              onClick={() => setSelectedTest(test)}
              isCompleted={completedTests[test.id]?.completed}
            >
              <h3>{test.title}</h3>
              <p>{test.description}</p>
              {completedTests[test.id]?.completed && (
                <CompletionBadge score={completedTests[test.id].score}>
                  Score: {completedTests[test.id].score}/10
                </CompletionBadge>
              )}
              <div style={{ color: '#666', fontSize: '0.95rem' }}>
                <div style={{ marginBottom: '0.5rem' }}>⏱ 18 minutes</div>
                <div>📝 10 questions</div>
                {completedTests[test.id]?.date && (
                  <div style={{ marginTop: '1rem', color: '#888', fontSize: '0.9rem' }}>
                    Completed: {new Date(completedTests[test.id].date).toLocaleDateString()}
                  </div>
                )}
              </div>
            </TestCard>
          ))}
        </TestSelection>
      </Container>
    );
  }

  return (
    <Container>
      <TopBar>
        <Logo>
          <LogoIcon>TT</LogoIcon>
          TOEFL Top
        </Logo>
        <BackButton onClick={handleBack}>← Back to Test Selection</BackButton>
      </TopBar>
      <Timer timeLeft={timeLeft}>
        Time Left: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
      </Timer>
      <TestContent>
        <ReadingPassage>
          <h2>{selectedTest.title}</h2>
          {selectedTest.paragraphs.map((para, index) => (
            <div key={index}>
              <h3>{para.heading}</h3>
              <p>{para.content}</p>
            </div>
          ))}
        </ReadingPassage>
        <QuestionsPanel>
          <h2>Questions</h2>
          {selectedTest.questions.map((q, index) => (
            <Question key={index}>
              <h3>{index + 1}. {q.question}</h3>
              {q.options.map((option, optIndex) => (
                <label key={optIndex}>
                  <input
                    type="radio"
                    name={`q${index}`}
                    value={optIndex}
                    checked={userAnswers[`q${index}`] === optIndex}
                    onChange={(e) => setUserAnswers({
                      ...userAnswers,
                      [`q${index}`]: parseInt(e.target.value)
                    })}
                  />
                  {option}
                </label>
              ))}
            </Question>
          ))}
          <button
            style={{
              float: 'right',
              padding: '1rem 2rem',
              background: '#2c3e50',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 500,
              transition: 'background 0.2s',
              ':hover': {
                background: '#34495e'
              }
            }}
            onClick={handleSubmitTest}
          >
            Submit Test
          </button>
        </QuestionsPanel>
      </TestContent>
    </Container>
  );
};

export default ReadingTest; 